import { useMemo, useState } from "react";
import { ArrowRight, ArrowLeft, RotateCcw, Sparkles, CheckCircle2 } from "lucide-react";

const STEPS = [
  {
    key: "env",
    title: "Where will the screen live?",
    hint: "This drives brightness and IP rating.",
    options: [
      { value: "indoor", label: "Indoor" },
      { value: "semi", label: "Semi-outdoor" },
      { value: "outdoor", label: "Outdoor" },
      { value: "studio", label: "Broadcast studio" },
    ],
  },
  {
    key: "distance",
    title: "Closest viewer distance?",
    hint: "The pixel pitch (mm) roughly matches minimum viewing distance (m).",
    options: [
      { value: "1", label: "Under 2 m" },
      { value: "2", label: "2 – 5 m" },
      { value: "3", label: "5 – 15 m" },
      { value: "4", label: "15 m and beyond" },
    ],
  },
  {
    key: "content",
    title: "Primary content?",
    hint: "We tune refresh rate and color for your use case.",
    options: [
      { value: "static", label: "Static branding" },
      { value: "video", label: "Video / creative" },
      { value: "broadcast", label: "Live broadcast" },
      { value: "data", label: "Data / dashboards" },
    ],
  },
];

const recommend = ({ env, distance, content }) => {
  const map = {
    indoor: {
      "1": { pitch: "P1.5 – P1.8", nits: "500 – 800",  ip: "Indoor rated" },
      "2": { pitch: "P2.5 – P3",   nits: "800 – 1,200", ip: "Indoor rated" },
      "3": { pitch: "P4 – P6",     nits: "1,200 – 1,800", ip: "Indoor rated" },
      "4": { pitch: "P8 – P10",    nits: "1,800 – 2,500", ip: "Indoor rated" },
    },
    semi: {
      "1": { pitch: "P2 – P2.5",   nits: "1,500 – 2,500", ip: "IP54 front" },
      "2": { pitch: "P3 – P4",     nits: "2,500 – 3,500", ip: "IP54 front" },
      "3": { pitch: "P5 – P6",     nits: "3,500 – 5,000", ip: "IP54 front" },
      "4": { pitch: "P8 – P10",    nits: "5,000 – 6,500", ip: "IP54 front" },
    },
    outdoor: {
      "1": { pitch: "P2.5 – P3",   nits: "3,500 – 5,000", ip: "IP65 front / IP54 rear" },
      "2": { pitch: "P4 – P5",     nits: "5,000 – 6,500", ip: "IP65 front / IP54 rear" },
      "3": { pitch: "P6 – P8",     nits: "6,500 – 7,500", ip: "IP65 front / IP54 rear" },
      "4": { pitch: "P10 – P16",   nits: "7,500 – 9,000", ip: "IP65 front / IP54 rear" },
    },
    studio: {
      "1": { pitch: "P1.2 – P1.5", nits: "600 – 1,000", ip: "HDR calibrated" },
      "2": { pitch: "P1.5 – P1.8", nits: "800 – 1,200", ip: "HDR calibrated" },
      "3": { pitch: "P2 – P2.5",   nits: "1,000 – 1,500", ip: "HDR calibrated" },
      "4": { pitch: "P2.5 – P3",   nits: "1,200 – 1,800", ip: "HDR calibrated" },
    },
  };
  const base = map[env]?.[distance] || map.indoor["2"];
  const refresh =
    content === "broadcast" ? "3,840 Hz (broadcast-safe)"
    : content === "video"   ? "1,920 Hz+"
    : content === "data"    ? "1,920 Hz"
    :                         "960 Hz+";
  return { ...base, refresh };
};

const SpecFinder = () => {
  const [i, setI] = useState(0);
  const [ans, setAns] = useState({});
  const done = i >= STEPS.length;
  const step = STEPS[i];
  const result = useMemo(() => (done ? recommend(ans) : null), [done, ans]);

  const pick = (val) => {
    setAns((a) => ({ ...a, [step.key]: val }));
    setTimeout(() => setI((n) => n + 1), 220);
  };
  const back = () => setI((n) => Math.max(0, n - 1));
  const reset = () => { setAns({}); setI(0); };

  const progress = done ? 100 : Math.round((i / STEPS.length) * 100);

  return (
    <section id="spec-finder" data-testid="spec-finder" className="relative bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#00439C] flex items-center gap-2 mb-4">
              <Sparkles size={14} /> Spec finder
            </div>
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl leading-[1.05] text-[#0B1220] mb-5">
              Find the right<br /> display in <span className="text-[#00439C]">60 seconds.</span>
            </h2>
            <p className="text-[#475569] text-base lg:text-lg leading-relaxed">
              Answer three quick questions and we’ll suggest the pixel pitch,
              brightness, and refresh rate our engineers would specify for your site.
            </p>
            <div className="mt-8 pt-6 border-t hairline text-xs text-[#64748B] leading-relaxed">
              Indicative only — final spec is confirmed after a site survey by our engineering team.
            </div>
          </div>

          <div className="lg:col-span-8">
            <div
              data-testid="spec-finder-card"
              className="relative rounded-3xl bg-[#0B1220] text-white p-8 lg:p-10 overflow-hidden shadow-[0_30px_80px_-40px_rgba(11,18,32,0.5)]"
            >
              {/* progress */}
              <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-white/50 mb-3">
                <span data-testid="spec-finder-progress-label">
                  {done ? "Done" : `Step ${i + 1} of ${STEPS.length}`}
                </span>
                <span>{progress}%</span>
              </div>
              <div className="h-[2px] w-full bg-white/10 rounded-full overflow-hidden mb-8">
                <div
                  className="h-full bg-gradient-to-r from-[#00439C] to-[#3B82F6] transition-[width] duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {!done ? (
                <div key={i} className="rise">
                  <div className="text-xl lg:text-2xl font-display font-bold mb-1.5">
                    {step.title}
                  </div>
                  <div className="text-sm text-white/55 mb-7">{step.hint}</div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    {step.options.map((o) => {
                      const active = ans[step.key] === o.value;
                      return (
                        <button
                          key={o.value}
                          data-testid={`spec-opt-${step.key}-${o.value}`}
                          onClick={() => pick(o.value)}
                          className={`group text-left px-5 py-4 rounded-xl border transition-all
                            ${active
                              ? "bg-[#00439C] border-[#00439C]"
                              : "bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-white/25"}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-[15px] font-semibold">{o.label}</span>
                            <span className={`w-5 h-5 rounded-full border flex items-center justify-center
                              ${active ? "border-white bg-white" : "border-white/25 group-hover:border-white/50"}`}>
                              {active && <span className="w-2 h-2 rounded-full bg-[#00439C]" />}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-8 flex items-center justify-between">
                    <button
                      data-testid="spec-finder-back"
                      onClick={back}
                      disabled={i === 0}
                      className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
                    >
                      <ArrowLeft size={14} /> Back
                    </button>
                    <div className="flex gap-1.5">
                      {STEPS.map((_, k) => (
                        <span
                          key={k}
                          className={`w-1.5 h-1.5 rounded-full ${k <= i ? "bg-white" : "bg-white/20"}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div data-testid="spec-finder-result" className="rise">
                  <div className="flex items-center gap-2 text-[#3B82F6] text-xs uppercase tracking-widest font-semibold mb-3">
                    <CheckCircle2 size={14} /> Recommended spec
                  </div>
                  <div className="text-2xl lg:text-3xl font-display font-bold leading-tight mb-8">
                    Here’s where our engineers would start.
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <ResultRow label="Pixel pitch" value={result.pitch} testid="result-pitch" />
                    <ResultRow label="Brightness" value={`${result.nits} nits`} testid="result-nits" />
                    <ResultRow label="Refresh rate" value={result.refresh} testid="result-refresh" />
                    <ResultRow label="Enclosure" value={result.ip} testid="result-ip" />
                  </div>

                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <a
                      href="#contact"
                      data-testid="spec-finder-cta"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0B1220] text-sm font-semibold rounded-md hover:bg-[#F5F7FB] transition"
                    >
                      Get an exact quote <ArrowRight size={16} />
                    </a>
                    <button
                      data-testid="spec-finder-reset"
                      onClick={reset}
                      className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white transition"
                    >
                      <RotateCcw size={14} /> Start over
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ResultRow = ({ label, value, testid }) => (
  <div
    data-testid={testid}
    className="rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4"
  >
    <div className="text-[11px] uppercase tracking-widest text-white/45 mb-1">{label}</div>
    <div className="text-lg font-display font-bold">{value}</div>
  </div>
);

export default SpecFinder;
